import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { workspaceChatbotAdminClient } from "@/api/workspaceChatbotAdminApi";
import {
  describeActiveCombo,
  flagsFromState,
  validateFlagsAgainstAllowlist,
  validateWidgetFlags,
} from "@/lib/widgetFlagValidation";
import KnowledgeBaseSection from "@/pages/settings/knowledge/KnowledgeBaseSection";

function Toggle({ on, onToggle, id }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      id={id}
      onClick={onToggle}
      className={`inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
        on ? "ui-toggle-track-on" : "ui-toggle-track-off"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-slate-200/70 transition-transform duration-200 ease-out dark:bg-slate-200 dark:ring-slate-500 ${
          on ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function SectionShell({ title, description, width = "max-w-6xl", actions = null, children }) {
  return (
    <div className={`mx-auto w-full ${width} space-y-6`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h3>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function SectionCard({ children, className = "" }) {
  return <div className={`ui-card-static !p-6 sm:!p-8 ${className}`}>{children}</div>;
}

const CHATBOT_TABS = [
  { id: "automation", label: "Automation", icon: "ri-flashlight-line" },
  { id: "knowledge", label: "Knowledge base", icon: "ri-database-2-line" },
  { id: "prompt", label: "Dynamic prompt", icon: "ri-code-s-slash-line" },
];

function KnowledgeRagPanel({ ragEnabled, topK, maxContextChars, onChange, onSave, saving }) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        When enabled, chat-service may call kb-manager search during widget replies (requires{" "}
        <span className="font-mono text-[11px]">KB_MANAGER_BASE_URL</span> on chat-service). Tune chunk count and context
        budget here.
      </p>
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-5 dark:border-slate-700">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">RAG retrieval</p>
          <p className="mt-1 text-xs text-slate-500">Inject knowledge-base excerpts into the model context.</p>
        </div>
        <Toggle id="rag-enabled" on={ragEnabled} onToggle={() => onChange({ ragEnabled: !ragEnabled })} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="rag-topk" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-100">
            Top K articles
          </label>
          <input
            id="rag-topk"
            type="number"
            min={1}
            max={50}
            className="ui-field"
            value={topK}
            onChange={(e) => onChange({ topK: parseInt(e.target.value, 10) || 5 })}
          />
        </div>
        <div>
          <label htmlFor="rag-max" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-100">
            Max context characters
          </label>
          <input
            id="rag-max"
            type="number"
            min={500}
            max={32000}
            step={100}
            className="ui-field"
            value={maxContextChars}
            onChange={(e) => onChange({ maxContextChars: parseInt(e.target.value, 10) || 4000 })}
          />
        </div>
      </div>
      <button type="button" className="ui-btn-primary" disabled={saving} onClick={() => void onSave()}>
        {saving ? "Saving…" : "Save RAG settings"}
      </button>
    </div>
  );
}

function LlmPromptPanel({ botCharacter, aiClassifier, onChange, onSave, saving }) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        These map to <span className="font-mono text-[11px]">ai.workspace_llm_prompts</span> and are consumed by
        chat-service for widget replies.
      </p>
      <div>
        <label htmlFor="bot-character" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-100">
          Bot character (system prompt)
        </label>
        <textarea
          id="bot-character"
          rows={10}
          className="ui-field resize-y font-mono text-sm"
          value={botCharacter}
          onChange={(e) => onChange({ botCharacter: e.target.value })}
          placeholder="You are a helpful support assistant..."
        />
      </div>
      <div>
        <label htmlFor="ai-classifier" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-100">
          Handoff classifier prompt (optional)
        </label>
        <textarea
          id="ai-classifier"
          rows={6}
          className="ui-field resize-y font-mono text-sm"
          value={aiClassifier}
          onChange={(e) => onChange({ aiClassifier: e.target.value })}
          placeholder="Instructions for when to escalate to a human..."
        />
      </div>
      <button type="button" className="ui-btn-primary" disabled={saving} onClick={() => void onSave()}>
        {saving ? "Saving…" : "Save prompts"}
      </button>
    </div>
  );
}

export default function ChatbotSettingsSection() {
  const [chatbotTab, setChatbotTab] = useState("automation");
  const [loading, setLoading] = useState(true);
  const [savingFlags, setSavingFlags] = useState(false);
  const [savingRag, setSavingRag] = useState(false);
  const [savingPrompts, setSavingPrompts] = useState(false);

  const [flags, setFlags] = useState({
    enableChatbot: true,
    enableHumanAgents: false,
    enableTicketSystemIntegration: false,
  });
  const [allowedWidgetCombos, setAllowedWidgetCombos] = useState([1, 2, 3, 4, 5, 6]);
  const [rag, setRag] = useState({ ragEnabled: false, topK: 5, maxContextChars: 4000 });
  const [prompts, setPrompts] = useState({ botCharacter: "", aiClassifier: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r, p] = await Promise.all([
        workspaceChatbotAdminClient.getWidgetFlags(),
        workspaceChatbotAdminClient.getRagSettings(),
        workspaceChatbotAdminClient.getLlmPrompts(),
      ]);
      setFlags({
        enableChatbot: Boolean(f.enableChatbot),
        enableHumanAgents: Boolean(f.enableHumanAgents),
        enableTicketSystemIntegration: Boolean(f.enableTicketSystemIntegration),
      });
      if (Array.isArray(f.allowedWidgetCombos) && f.allowedWidgetCombos.length > 0) {
        setAllowedWidgetCombos(f.allowedWidgetCombos.map((x) => Number(x)));
      }
      setRag({
        ragEnabled: Boolean(r.ragEnabled),
        topK: Number(r.topK) || 5,
        maxContextChars: Number(r.maxContextChars) || 4000,
      });
      setPrompts({
        botCharacter: String(p.botCharacter || ""),
        aiClassifier: String(p.aiClassifier || ""),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load chatbot settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activeComboLabel = useMemo(() => describeActiveCombo(flags), [flags]);

  const saveFlags = async () => {
    const err =
      validateFlagsAgainstAllowlist(flags, allowedWidgetCombos) ||
      validateWidgetFlags(flagsFromState(flags));
    if (err) {
      toast.error(err);
      return;
    }
    setSavingFlags(true);
    try {
      await workspaceChatbotAdminClient.putWidgetFlags({ ...flags });
      toast.success("Widget feature flags saved.");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSavingFlags(false);
    }
  };

  const saveRag = async () => {
    setSavingRag(true);
    try {
      await workspaceChatbotAdminClient.putRagSettings({ ...rag });
      toast.success("RAG settings saved.");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSavingRag(false);
    }
  };

  const savePrompts = async () => {
    setSavingPrompts(true);
    try {
      await workspaceChatbotAdminClient.putLlmPrompts({
        botCharacter: prompts.botCharacter,
        aiClassifier: prompts.aiClassifier || null,
      });
      toast.success("Prompts saved.");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSavingPrompts(false);
    }
  };

  const previewFlow = () => {
    const clip = (prompts.botCharacter || "(empty system prompt)").slice(0, 800);
    toast.message("Prompt preview (first 800 chars)", { description: clip });
  };

  const toggleFlag = (key) => {
    setFlags((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const err = validateFlagsAgainstAllowlist(next, allowedWidgetCombos);
      if (err) {
        toast.error(err);
        return prev;
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <p className="text-sm text-slate-500">Loading chatbot settings…</p>
      </div>
    );
  }

  return (
    <SectionShell
      title="Chatbot"
      description="Widget feature flags, optional kb-manager RAG, and workspace LLM prompts (chat-service)."
      actions={
        <div
          className="flex flex-wrap gap-1 rounded-xl bg-slate-100/90 p-1 ring-1 ring-slate-200/80 dark:bg-slate-800/80 dark:ring-slate-700"
          role="tablist"
          aria-label="Chatbot settings sections"
        >
          {CHATBOT_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              title={t.label}
              aria-selected={chatbotTab === t.id}
              onClick={() => setChatbotTab(t.id)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
                chatbotTab === t.id
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              }`}
            >
              <i className={t.icon} aria-hidden />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>
      }
    >
      {chatbotTab === "automation" && (
        <SectionCard className="space-y-6">
          <div className="rounded-2xl border border-teal-200/60 bg-gradient-to-r from-teal-50 to-cyan-50 px-4 py-4 sm:px-5 dark:border-teal-900/40 dark:from-teal-950/40 dark:to-cyan-950/30">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Widget routing</p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  These map to <span className="font-mono text-[11px]">ai.workspace_widget_feature_flags</span> and are
                  read by chat-service per workspace. Only routing modes allowed by your platform operator can be saved.
                </p>
              </div>
              <div className="inline-flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-600">
                  <span className={`h-2 w-2 rounded-full ${flags.enableChatbot ? "bg-emerald-500" : "bg-slate-300"}`} />
                  {flags.enableChatbot ? "Chatbot on" : "Chatbot off"}
                </span>
                <span className="rounded-full bg-teal-600/10 px-3 py-1 text-[11px] font-semibold text-teal-800 dark:text-teal-200">
                  Active: {activeComboLabel}
                </span>
              </div>
            </div>
          </div>

          {[
            {
              key: "enableChatbot",
              label: "Enable chatbot",
              desc: "Allow bot replies on the widget before human pickup.",
            },
            {
              key: "enableHumanAgents",
              label: "Enable human agents",
              desc: "Expose human handoff and agent workflows for this workspace.",
            },
            {
              key: "enableTicketSystemIntegration",
              label: "Ticket system integration",
              desc: "Agent-created tickets and widget notifications. Works with chatbot and/or human agents (Human + tickets = no bot, handoff-only with ticket events).",
            },
          ].map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between gap-4 border-b border-slate-100 pb-5 last:border-0 last:pb-0 dark:border-slate-700"
            >
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.label}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.desc}</p>
              </div>
              <Toggle id={`f-${item.key}`} on={flags[item.key]} onToggle={() => toggleFlag(item.key)} />
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-5 dark:border-slate-700">
            <button type="button" className="ui-btn-primary" disabled={savingFlags} onClick={() => void saveFlags()}>
              {savingFlags ? "Saving…" : "Save chatbot settings"}
            </button>
            <button type="button" className="ui-btn-secondary" onClick={() => previewFlow()}>
              Preview prompt snippet
            </button>
          </div>
        </SectionCard>
      )}

      {chatbotTab === "knowledge" && (
        <KnowledgeBaseSection
          ragEnabled={rag.ragEnabled}
          topK={rag.topK}
          maxContextChars={rag.maxContextChars}
          onRagChange={(patch) => setRag((prev) => ({ ...prev, ...patch }))}
          onRagSave={saveRag}
          savingRag={savingRag}
        />
      )}

      {chatbotTab === "prompt" && (
        <SectionCard>
          <LlmPromptPanel
            botCharacter={prompts.botCharacter}
            aiClassifier={prompts.aiClassifier}
            onChange={(patch) => setPrompts((prev) => ({ ...prev, ...patch }))}
            onSave={savePrompts}
            saving={savingPrompts}
          />
        </SectionCard>
      )}
    </SectionShell>
  );
}
